import type { SelectionItem } from '@/domain/types';
import type { CheckoutContext, ControllerSheet } from './controllerTypes';

export interface ShowErrorOptions {
  retry?: () => void;
  autoDismissMs?: number;
  helpCode?: string;
}

// The retry of the sheet closes the sheet first. The key stays without a
// retry, thus each error sheet has the same shape.
export function createShowError(
  setSheet: (sheet: ControllerSheet) => void
): (titleKey: string, bodyKey: string, options?: ShowErrorOptions) => void {
  return (titleKey, bodyKey, options = {}) => {
    const { retry, autoDismissMs, helpCode } = options;
    setSheet({
      kind: 'error',
      titleKey,
      bodyKey,
      ...(autoDismissMs ? { autoDismissMs } : {}),
      ...(helpCode ? { helpCode } : {}),
      retry: retry
        ? () => {
            setSheet(null);
            retry();
          }
        : undefined,
    });
  };
}

function randomToken(): string {
  return typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createIdempotencyKey(
  scope: 'payment' | 'print',
  externalEntrId?: string
): string {
  return ['entr', scope, externalEntrId, randomToken()]
    .filter(Boolean)
    .join('-');
}

// One nonce per visit (minted at session start / reset-to-attract, held across
// BACK + retry within the visit). ENTR's session_id is STABLE per group, so
// without this an identity of (session, amount, selection) alone is identical
// on every visit — Stripe's idempotency then replays the already-succeeded
// PaymentIntent and a re-scan with the same selection prints for free, forever.
export function createEpisodeNonce(): string {
  return randomToken();
}

export function createCheckoutIdentity(context: CheckoutContext): string {
  return [
    context.sessionId,
    context.episodeNonce,
    context.quote.amountDueCents,
    context.quote.currency,
    createSelectionIdentity(context.items),
  ].join(':');
}

// Deterministic Stripe idempotency key for the ENTR card charge. Derived from the
// checkout identity (session + episode + amount + currency + selection) rather
// than a per-attempt random UUID, so a BACK-then-retry of the same selection
// re-sends the SAME key and Stripe returns the original PaymentIntent instead of
// authorizing a second charge. The episode nonce bounds that determinism to ONE
// visit: a later visit mints a new nonce, so the key can never replay a previous
// visit's settled PaymentIntent (the paid-once-print-forever reentrancy).
// Hashed to stay under Stripe's 255-char idempotency-key limit for multi-photo carts.
export function createPaymentIdempotencyKey(context: CheckoutContext): string {
  return `entr-payment-${stableHash(createCheckoutIdentity(context))}`;
}

// Identity of the ORDER, sent to the backend so it can tell a retry of an
// already-captured order (same key) from a genuine second purchase in the same
// session (different key). Hashed for the same reason the idempotency key above
// is: the raw identity carries one `type/id/printImageUuid/quantity` tuple per
// photo, so a three-photo cart already runs past the request field's 255-char
// limit. Only equality is ever asked of it, so a bounded digest answers it just
// as well. Prefixed differently from the idempotency key because both ride the
// same request and must not be mistaken for one another.
export function createCheckoutKey(context: CheckoutContext): string {
  return `entr-checkout-${stableHash(createCheckoutIdentity(context))}`;
}

// Keyed on OUR identity (sessionId + selection), NOT ticketCode — sessionId is
// per-checkout so the derived eventKey stays stable and now works for browse
// sessions that carry no ticketCode. Deliberately NO episodeNonce here (unlike
// the payment identity): the durable coverage count in localStorage must
// survive a mid-print reload so the retry visit records the DELTA, not the
// full count again. The trade-off — a later same-selection sale dedupes its
// writeback — is the long-standing ledger behavior, not a revenue hole.
export function createPrintedCoverageKey(context: CheckoutContext): string {
  return [context.sessionId, createSelectionIdentity(context.items)].join(':');
}

export function createSelectionIdentity(items: SelectionItem[]): string {
  return items
    .map(
      (item) =>
        `${item.photo.type}/${item.photo.id}/${item.photo.printImageUuid ?? ''}/${item.quantity}`
    )
    .join('|');
}

// 106-bit non-cryptographic string hash (two cyrb53 passes) — synchronous,
// bounded-length, collision-negligible for per-kiosk checkout identities. Used only
// to bound deterministic idempotency keys; it is not a security primitive.
export function stableHash(input: string): string {
  return `${cyrb53(input, 0).toString(36)}${cyrb53(input, 1).toString(36)}`;
}

function cyrb53(input: string, seed: number): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < input.length; i += 1) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}
